// src/pages/SuperAdmin/components/CreateBusinessModal.tsx - Modal de création complète avec tous les champs

import React, { useState } from 'react';
import {
  Modal,
  Drawer,
  Form,
  Input,
  Select,
  Button,
  Steps,
  Card,
  Row,
  Col,
  InputNumber,
  Switch,
  Space,
  Typography,
  Alert,
  Divider,
  ColorPicker,
} from 'antd';
import {
  ShopOutlined,
  SettingOutlined,
  CheckOutlined,
  BgColorsOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import { useCreateBusiness, CreateBusinessData } from '../../../hooks/useSuperAdmin';
import { BusinessType, BUSINESS_TYPE_CONFIGS, businessSupportsInventoryConfig } from '../../../types/business';
import { generateSMSSenderName } from '../../../types/business';
import TaxConfigurationStep from './TaxConfigurationStep';
import ExpenseCategoriesStep from './ExpenseCategoriesStep';


const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

interface CreateBusinessModalProps {
  visible: boolean;
  onClose: () => void;
  /** Called on success; when renderAsPage, businessId is passed for navigation */
  onSuccess: (businessId?: string) => void;
  /** When true, render as full page content instead of Drawer (UX refactor) */
  renderAsPage?: boolean;
}

const CreateBusinessModal: React.FC<CreateBusinessModalProps> = ({
  visible,
  onClose,
  onSuccess,
  renderAsPage = false,
}) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const createBusinessMutation = useCreateBusiness();

  const [businessData, setBusinessData] = useState<Partial<CreateBusinessData>>({});
  const [selectedBusinessType, setSelectedBusinessType] = useState<BusinessType | null>(null);

  // Reset lors de l'ouverture/fermeture
  const handleClose = () => {
    setCurrentStep(0);
    setBusinessData({});
    setSelectedBusinessType(null);
    form.resetFields();
    onClose();
  };

  // Navigation entre étapes
  const nextStep = async () => {
    try {
      const stepFields = getStepFields(currentStep);
      const values = await form.validateFields(stepFields);
      
      setBusinessData(prev => ({ ...prev, ...values }));
      setCurrentStep(prev => prev + 1);
    } catch (error) {
      console.log('Validation échouée:', error);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Définir les champs par étape selon le type de business
  const getStepFields = (step: number): string[] => {
    switch (step) {
      case 0:
        return ['businessName', 'businessType'];
      case 1:
        return ['smsSenderName', 'phone', 'whatsappNumber', 'address', 'description'];
      case 2:
        return ['primaryColor', 'secondaryColor'];
      case 3:
        // Pour les business avec stocks, on valide les champs de stocks
        if (showInventoryConfig) {
          return ['bonusPercentage', 'minimumUsage', 'maxActiveCampaigns', 'lowStockThreshold'];
        }
        // Pour les autres business, on valide tout sauf les stocks
        return ['bonusPercentage', 'minimumUsage', 'maxActiveCampaigns'];
      case 4:
        return ['taxConfiguration'];
      default:
        return [];
    }
  };

  // Soumission finale
  const handleSubmit = async () => {
    try {
      // Valider SEULEMENT les champs de l'étape courante (étape 4 - TVA)
      const finalStepValues = await form.validateFields(getStepFields(currentStep));      
      // Fusionner avec les données accumulées des étapes précédentes
      const completeValues = {
        ...businessData,     // Données des étapes 1-3
        ...finalStepValues   // Données de l'étape 4
      };
      
      console.log('=== DEBUG MERGED DATA ===');
      console.log('completeValues:', completeValues);
      
      // Vérifier que les données essentielles sont présentes
      if (!completeValues.businessName || completeValues.businessName.trim() === '') {
        throw new Error('Le nom du business est requis');
      }

      // Traitement des couleurs (ColorPicker retourne un objet Color)
      const primaryColor = completeValues.primaryColor instanceof Object 
        ? completeValues.primaryColor.toHexString() 
        : completeValues.primaryColor || '#667eea';
      
      const secondaryColor = completeValues.secondaryColor instanceof Object 
        ? completeValues.secondaryColor.toHexString() 
        : completeValues.secondaryColor || '#764ba2';

        console.log('=== DEBUG COMPLETE VALUES ===');
      console.log('completeValues:', completeValues);
      console.log('completeValues.taxConfiguration:', completeValues.taxConfiguration);
      console.log('Type:', typeof completeValues.taxConfiguration);
      
      // Configuration standard
      const configuration: any = {
          loyalty: {
            enabled: completeValues.loyaltyEnabled !== false,  // ✅ NOUVEAU : Par défaut true
            bonusPercentage: completeValues.bonusPercentage || 3,
            minimumUsage: completeValues.minimumUsage || 1000,
          },
          marketing: {
            maxActiveCampaigns: completeValues.maxActiveCampaigns,
          },
          inventory: {
            enabled: true,
            lowStockThreshold: completeValues.lowStockThreshold || 5,
          },
          tax: completeValues.taxConfiguration,
          // ✅ NOUVEAU : Catégories de dépenses
          expenses: completeValues.expenseCategories ? {
            categories: completeValues.expenseCategories.categories || [],
          } : {
            categories: [],
          },
        };

      const completeData: CreateBusinessData = {
        businessName: completeValues.businessName.trim(),
        businessType: completeValues.businessType,
        configuration,
        
        branding: {
          smsSenderName: completeValues.smsSenderName?.trim(),
          phone: completeValues.phone?.trim(),
          whatsappNumber: completeValues.whatsappNumber?.trim(),
          address: completeValues.address?.trim(),
          description: completeValues.description?.trim() || undefined,
          primaryColor: primaryColor,
          secondaryColor: secondaryColor,
        },
        
        // ✅ NOUVEAU : Admin optionnel (peut être créé après)
        adminName: completeValues.adminName?.trim() || undefined,
        adminEmail: completeValues.adminEmail?.trim() || undefined,
        adminPassword: completeValues.adminPassword || undefined,
      };

      console.log('=== FINAL DATA TO SEND ===');
      console.log('completeData:', completeData);

      const result = await createBusinessMutation.mutateAsync(completeData);
      onSuccess(result?.businessId);
      handleClose();
      
    } catch (error) {
      console.error('Erreur création business:', error);
    }
  };

  // Auto-génération nom SMS
  const handleBusinessNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (name.length >= 3) {
      const autoSmsName = generateSMSSenderName(name);
      form.setFieldValue('smsSenderName', autoSmsName);
    }
  };

  // Gestion du changement de type de business
  const handleBusinessTypeChange = (value: BusinessType) => {
    setSelectedBusinessType(value);
  };

  // Vérifier si le type de business nécessite certains paramètres
  const showInventoryConfig = businessSupportsInventoryConfig(selectedBusinessType);

  // Étapes du wizard
  const steps = [
    {
      title: 'Business',
      icon: <ShopOutlined />,
    },
    {
      title: 'Contact',
      icon: <SettingOutlined />,
    },
    {
      title: 'Couleurs',
      icon: <BgColorsOutlined />,
    },
    {
      title: 'Configuration',
      icon: <SettingOutlined />,
    },
    {
      title: 'TVA',
      icon: <CalculatorOutlined />,
    },
  ];

  const footerButtons = (
    <Space>
      <Button onClick={handleClose}>Annuler</Button>
      {currentStep > 0 && <Button onClick={prevStep}>Précédent</Button>}
      {currentStep < 4 ? (
        <Button type="primary" onClick={nextStep}>Suivant</Button>
      ) : (
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSubmit}
          loading={createBusinessMutation.isPending}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Créer le Business
        </Button>
      )}
    </Space>
  );

  if (!visible && !renderAsPage) return null;

  const formContent = (
    <div style={{ padding: '8px 0' }}>
      <Steps current={currentStep} items={steps} style={{ marginBottom: '16px' }} />

        <Form
          form={form}
          layout="vertical"
          size="large"
          initialValues={{
            loyaltyEnabled: true,    // ✅ NOUVEAU : Activée par défaut
            bonusPercentage: 3,
            minimumUsage: 1000,
            inventoryEnabled: false,
            lowStockThreshold: 5,
            primaryColor: '#667eea',
            secondaryColor: '#764ba2',
            taxConfiguration: {
              defaultRateId: 'cameroon_standard',
              selectedRates: ['cameroon_standard'],
              displayMode: 'inclusive',
              showTaxOnReceipts: true,
              companyTaxNumber: '',
            },
            // ✅ NOUVEAU : Catégories de dépenses par défaut
            expenseCategories: {
              categories: [],
            },
          }}
        >
          {/* ÉTAPE 1: Informations Business */}
          {currentStep === 0 && (
            <Card title="Informations du Business" size="small">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="businessName"
                    label="Nom du Business"
                    rules={[
                      { required: true, message: 'Nom requis' },
                      { min: 3, message: 'Minimum 3 caractères' },
                      { max: 50, message: 'Maximum 50 caractères' },
                    ]}
                  >
                    <Input
                      placeholder="Ex: Institut Belle Époque"
                      onChange={handleBusinessNameChange}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="businessType"
                    label="Type de Business"
                    rules={[{ required: true, message: 'Type requis' }]}
                  >
                    <Select 
                      placeholder="Sélectionner le type d'activité"
                      onChange={handleBusinessTypeChange}
                      optionLabelProp="label"
                    >
                      {Object.entries(BUSINESS_TYPE_CONFIGS).map(([key, config]) => (
                        <Option key={key} value={key} label={config.name}>
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{config.name}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {config.description}
                            </div>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

            </Card>
          )}

          {/* ÉTAPE 2: Contact & Informations */}
          {currentStep === 1 && (
            <Card title="Informations de Contact" size="small">
              <Alert
                message="Ces informations seront utilisées pour les SMS et communications client"
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="smsSenderName"
                    label="Nom expéditeur SMS (max 11 caractères)"
                    rules={[
                      { required: true, message: 'Nom SMS requis' },
                      { max: 11, message: 'Maximum 11 caractères' },
                      { pattern: /^[A-Z0-9]+$/, message: 'Seulement lettres majuscules et chiffres' },
                    ]}
                  >
                    <Input 
                      placeholder="BELLEEPOQUE"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="phone"
                    label="Numéro de téléphone"
                    rules={[
                      { required: true, message: 'Téléphone requis' },
                      { pattern: /^237[0-9]{9}$/, message: 'Format: 237123456789' },
                    ]}
                  >
                    <Input placeholder="237123456789" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="whatsappNumber"
                    label="Numéro WhatsApp"
                    rules={[
                      { required: true, message: 'WhatsApp requis' },
                      { pattern: /^237[0-9]{9}$/, message: 'Format: 237123456789' },
                    ]}
                  >
                    <Input placeholder="237123456789" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Button 
                    type="dashed" 
                    onClick={() => {
                      const phone = form.getFieldValue('phone');
                      if (phone) {
                        form.setFieldValue('whatsappNumber', phone);
                      }
                    }}
                    style={{ marginTop: '30px', width: '100%' }}
                  >
                    Copier depuis téléphone
                  </Button>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="address"
                    label="Adresse complète"
                    rules={[
                      { required: true, message: 'Adresse requise' },
                      { min: 10, message: 'Adresse trop courte' },
                    ]}
                  >
                    <TextArea 
                      rows={3}
                      placeholder="Adresse complète du business..."
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label="Description (optionnelle)"
                  >
                    <TextArea 
                      rows={2}
                      placeholder="Description courte du business..."
                      maxLength={200}
                      showCount
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {/* ÉTAPE 3: Couleurs de marque */}
          {currentStep === 2 && (
            <Card title="Couleurs de la marque" size="small">
              <Alert
                message="Ces couleurs seront utilisées dans l'interface et les communications"
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />

              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="primaryColor"
                    label="Couleur principale"
                    rules={[{ required: true, message: 'Couleur principale requise' }]}
                  >
                    <ColorPicker
                      size="large"
                      showText
                      format="hex"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Utilisée pour les boutons principaux, headers
                  </Text>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="secondaryColor"
                    label="Couleur secondaire"
                    rules={[{ required: true, message: 'Couleur secondaire requise' }]}
                  >
                    <ColorPicker
                      size="large"
                      showText
                      format="hex"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Utilisée pour les accents, dégradés
                  </Text>
                </Col>
              </Row>

              {/* Aperçu des couleurs */}
              <Divider>Aperçu</Divider>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div 
                  style={{ 
                    width: '100px', 
                    height: '40px', 
                    backgroundColor: form.getFieldValue('primaryColor')?.toHexString?.() || form.getFieldValue('primaryColor') || '#667eea',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  Principale
                </div>
                <div 
                  style={{ 
                    width: '100px', 
                    height: '40px', 
                    backgroundColor: form.getFieldValue('secondaryColor')?.toHexString?.() || form.getFieldValue('secondaryColor') || '#764ba2',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  Secondaire
                </div>
                <div 
                  style={{ 
                    width: '120px', 
                    height: '40px', 
                    background: `linear-gradient(135deg, ${
                      form.getFieldValue('primaryColor')?.toHexString?.() || form.getFieldValue('primaryColor') || '#667eea'
                    }, ${
                      form.getFieldValue('secondaryColor')?.toHexString?.() || form.getFieldValue('secondaryColor') || '#764ba2'
                    })`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                >
                  Dégradé
                </div>
              </div>
            </Card>
          )}

          {/* ÉTAPE 4: Configuration */}
          {currentStep === 3 && (
            <Card title="Configuration du Business" size="small">
              
                <>
                  <Title level={5}>Programme de fidélité</Title>
                  <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col span={24}>
                      <Form.Item
                        name="loyaltyEnabled"
                        label="Activer le programme de fidélité (cagnotte)"
                        valuePropName="checked"
                        tooltip="Active ou désactive le système de cagnotte pour ce business"
                      >
                        <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="bonusPercentage"
                        label="Pourcentage de bonus (%)"
                        tooltip="Pourcentage accordé en cagnotte sur chaque prestation"
                      >
                        <InputNumber
                          min={0}
                          max={20}
                          step={0.5}
                          style={{ width: '100%' }}
                          addonAfter="%"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="minimumUsage"
                        label="Seuil d'utilisation (FCFA)"
                        tooltip="Montant minimum pour utiliser la cagnotte"
                      >
                        <InputNumber
                          min={500}
                          max={10000}
                          step={500}
                          style={{ width: '100%' }}
                          addonAfter="FCFA"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              

              
                <>
                  <Title level={5}>Marketing</Title>
                  <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                      <Form.Item
                        name="maxActiveCampaigns"
                        label="Campagnes actives max (optionnel)"
                        tooltip="Limite le nombre de campagnes marketing simultanées"
                      >
                        <InputNumber
                          min={1}
                          max={50}
                          style={{ width: '100%' }}
                          placeholder="Illimité"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              

              {showInventoryConfig && (
                <>
                  <Title level={5}>Gestion des stocks</Title>
                  <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                      <Form.Item
                        name="lowStockThreshold"
                        label="Seuil d'alerte stock faible"
                        tooltip="Nombre d'unités en dessous duquel une alerte est déclenchée"
                      >
                        <InputNumber
                          min={1}
                          max={100}
                          style={{ width: '100%' }}
                          addonAfter="unités"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Alert
                        type="info"
                        showIcon
                        message="Activation par feature"
                        description="Le module Stock / Inventaire s’active via les features du business. Ici, vous définissez seulement le seuil d’alerte."
                      />
                    </Col>
                  </Row>
                </>
              )}

              
                <>
                  <Divider />
                  <ExpenseCategoriesStep
                    value={form.getFieldValue('expenseCategories')}
                    onChange={(value) => {
                      form.setFieldsValue({ expenseCategories: value });
                      setBusinessData(prev => ({ ...prev, expenseCategories: value }));
                    }}
                  />
                </>
              

              <Text type="secondary" style={{ fontSize: '12px', marginTop: '16px', display: 'block' }}>
                Ces paramètres peuvent être modifiés plus tard dans les paramètres du business
              </Text>
            </Card>
          )}

          {/* ÉTAPE 5: Configuration TVA */}
          {currentStep === 4 && (
            <TaxConfigurationStep
              value={form.getFieldValue('taxConfiguration')}
              onChange={(value) => {
                console.log('=== SETTING TAX CONFIG IN FORM ===', value);
                form.setFieldsValue({ taxConfiguration: value });
                // Forcer la mise à jour immédiate
                setBusinessData(prev => ({ ...prev, taxConfiguration: value }));
              }}
            />
          )}
        </Form>
      </div>
  );

  if (renderAsPage) {
    return (
      <div style={{ padding: '24px 0' }}>
        {formContent}
        <div style={{ marginTop: 24 }}>{footerButtons}</div>
      </div>
    );
  }

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShopOutlined style={{ color: '#722ed1' }} />
          <span>Créer un nouveau Business</span>
        </div>
      }
      open={visible}
      onClose={handleClose}
      width="50%"
      destroyOnClose
      extra={footerButtons}
    >
      {formContent}
    </Drawer>
  );
};

export default CreateBusinessModal;